import React from "react"
import toast from "react-hot-toast"
import { useApp } from "../context/AppContext"
import { Icon, useT, useIsMobile } from "../shared"
import { useOrgName } from "../shared/BrandLogo"
import { Stat, LevelBadge, Progress, LEVEL_COLORS, Btn, Badge, Tabs } from "../shared/components"
import { tierShort, tierLabelOrEmpty } from "../lib/tier"
import {
  myCertificates,
  verifyCertificate,
  getCertificateTemplate,
  updateCertificateTemplate,
  uploadCertificateAsset,
  certAnalytics
} from "../api/certificates.api"
import { analyticsOverview } from "../api/users.api"
import { BarChart, LineChart, HBarChart, Heatmap } from "../shared/charts"
import { fmtNum, fmtDateShort, fmtDateLong } from "../shared/format"

/* ============================================================
   Certificate + Verification + Progress Analytics
   ============================================================ */

// =============================================================
// CERTIFICATE
// =============================================================
function CertificateScreen() {
  const t = useT();
  const { lang, navigate, role, currentProgramId, currentProgram } = useApp();
  const isMobile = useIsMobile();
  const [tab, setTab] = React.useState(role === "admin" ? "template" : "mine");
  const [certs, setCerts] = React.useState([]);
  const [selectedCertNo, setSelectedCertNo] = React.useState("");
  const [verifyResult, setVerifyResult] = React.useState(null);
  const [verifying, setVerifying] = React.useState(false);

  // Sync tab state reactively when role loaded/changed
  React.useEffect(() => {
    if (role === "admin") {
      setTab("template");
    } else {
      setTab("mine");
    }
  }, [role]);

  React.useEffect(() => {
    if (role !== "admin") {
      myCertificates().then(setCerts).catch(() => setCerts([]));
    }
  }, [role]);

  const featured = certs[0];

  async function handleVerify() {
    if (!selectedCertNo.trim()) return;
    setVerifying(true);
    setVerifyResult(null);
    try {
      const data = await verifyCertificate(selectedCertNo.trim());
      setVerifyResult(data);
    } catch (err) {
      const detail = err?.response?.data;
      setVerifyResult(detail || { valid: false, error: err.message });
    } finally {
      setVerifying(false);
    }
  }

  return (
    <div className="main-inner">
      <div className="page-header">
        <div className="page-header-text">
          <div className="eyebrow">
            {role === "admin"
              ? (lang === "id" ? "Admin · Sertifikat" : "Admin · Certificates")
              : (lang === "id" ? "Peserta · Sertifikat" : "Participant · Certificates")}
          </div>
          <h1 className="h1" style={{ marginTop: 6 }}>{t.certificates}</h1>
          <div className="muted mt-1">
            {role === "admin"
              ? (lang === "id" ? "Kustomisasi template visual & kelola verifikasi." : "Customize visual templates & manage verification.")
              : (lang === "id" ? "Sertifikat Anda. Setiap dilengkapi QR verifikasi publik." : "Your certificates. Each carries a publicly verifiable QR.")}
          </div>
        </div>
        <div className="page-header-actions">
          {role !== "admin" && (
            <Btn variant="secondary" icon={<Icon name="qrCode" size={14}/>} onClick={()=>setTab("verify")}>
              {t.verifyCert}
            </Btn>
          )}
        </div>
      </div>

      <Tabs
        tabs={
          role === "admin"
            ? [
                { id: "template", label: lang === "id" ? "Desain Template" : "Template Design" },
                { id: "analytics", label: lang === "id" ? "Analitik" : "Analytics" },
                { id: "verify", label: t.verifyCert },
              ]
            : [
                { id: "mine", label: lang === "id" ? "Sertifikat saya" : "My certificates", count: certs.length },
                { id: "verify", label: t.verifyCert },
              ]
        }
        value={tab}
        onChange={setTab}
      />

      {tab === "template" && role === "admin" && (
        <TemplateDesigner
          lang={lang}
          currentProgramId={currentProgramId}
          currentProgram={currentProgram}
        />
      )}

      {tab === "analytics" && role === "admin" && (
        <CredentialAnalytics lang={lang} programId={currentProgramId} isMobile={isMobile} />
      )}

      {tab === "mine" && role !== "admin" && (
        <div className="grid" style={{gridTemplateColumns: isMobile ? "1fr" : "1.4fr 1fr", gap: 24}}>
          {/* Big certificate preview */}
          <div>
            {featured ? (
              <>
                <GambarSertifikat key={featured.id} cert={featured} lang={lang} />
                <div className="row gap-2 mt-4">
                  {featured.pdf_url ? (
                    <a className="btn-primary" style={{textDecoration:"none", display:"inline-flex", alignItems:"center", gap:6, padding:"7px 12px", fontSize:13, fontWeight:500, background:"var(--program-color)", color:"white", borderRadius:"var(--r-md)", border:"1px solid transparent"}} href={featured.pdf_url} target="_blank" rel="noopener noreferrer">
                      <Icon name="download" size={14}/>{lang==="id"?"Download PDF":"Download PDF"}
                    </a>
                  ) : (
                    <Btn variant="primary" disabled icon={<Icon name="download" size={14}/>}>
                      {lang==="id"?"PDF belum tersedia":"PDF not ready"}
                    </Btn>
                  )}
                  {featured.public_id && (
                    <a className="btn secondary" style={{textDecoration:"none", display:"inline-flex", alignItems:"center", gap:6, padding:"7px 12px", fontSize:13, fontWeight:500}}
                       href={`/c/${featured.public_id}`} target="_blank" rel="noopener noreferrer">
                      <Icon name="link" size={14}/>{lang==="id"?"Halaman kredensial":"Credential page"}
                    </a>
                  )}
                  <Btn variant="secondary" icon={<Icon name="copy" size={14}/>} onClick={() => {
                    // Yang disalin alamat kredensialnya bila ada — itu yang bisa
                    // dibagikan dan dibuka siapa pun. Nomor sertifikat saja tidak
                    // membawa penerimanya ke mana-mana.
                    const teks = featured.public_id ? `${window.location.origin}/c/${featured.public_id}` : featured.cert_no
                    navigator.clipboard?.writeText(teks);
                    toast.success(lang==="id"?"Disalin":"Copied");
                  }}>
                    {featured.public_id
                      ? (lang==="id"?"Salin tautan":"Copy link")
                      : (lang==="id"?"Salin nomor":"Copy number")}
                  </Btn>
                </div>
              </>
            ) : (
              <div className="tile tile-pad" style={{textAlign:"center", padding:60}}>
                <Icon name="cert" size={48} color="var(--text-tertiary)"/>
                <h3 className="h3 mt-3">{lang==="id"?"Belum ada sertifikat":"No certificates yet"}</h3>
                <p className="muted small mt-2">
                  {lang==="id"
                    ? "Selesaikan kursus, posttest, dan kehadiran ≥ 80% untuk memenuhi syarat sertifikat."
                    : "Complete the course, posttest, and attendance ≥ 80% to qualify for a certificate."}
                </p>
              </div>
            )}
          </div>

          {/* Cert list */}
          <div className="col gap-3">
            <div className="tile">
              <div className="tile-header">
                <h3>{lang==="id"?"Semua sertifikat":"All certificates"}</h3>
                <span className="tile-header-sub mono">{certs.length}</span>
              </div>
              {certs.length === 0 && (
                <div className="tile-pad muted small" style={{textAlign:"center"}}>—</div>
              )}
              {certs.map((c, i) => (
                <div key={c.id} className="row gap-3" style={{padding:"14px 16px", borderTop: i>0?"1px solid var(--border)":"none", cursor:"pointer"}}>
                  <LevelBadge level={c.program?.level} label={tierShort(c.program)}/>
                  <div style={{flex:1, minWidth:0}}>
                    <div className="row gap-2">
                      <span style={{fontWeight:500}} className="small">{c.program?.name}</span>
                      {c.claimed_at ? <span className="tag green tiny"><span className="dot"/>{t.valid}</span>
                                    : <span className="tag amber tiny"><span className="dot"/>{lang==="id"?"Belum diklaim":"Not claimed"}</span>}
                    </div>
                    <div className="tiny muted mono mt-1">{c.cert_no}</div>
                    <div className="tiny muted mt-1">{lang==="id"?"Terbit":"Issued"} {new Date(c.issued_at).toLocaleDateString(lang==="id"?"id-ID":"en-US")}</div>
                  </div>
                  {c.public_id && (
                    <a className="btn sm ghost icon-only" href={`/c/${c.public_id}`} target="_blank" rel="noopener noreferrer"
                       title={lang==="id"?"Halaman kredensial":"Credential page"}>
                      <Icon name="link" size={12}/>
                    </a>
                  )}
                  {c.pdf_url && (
                    <a className="btn sm ghost icon-only" href={c.pdf_url} target="_blank" rel="noopener noreferrer" title="Download">
                      <Icon name="download" size={12}/>
                    </a>
                  )}
                </div>
              ))}
            </div>

            <div className="tile" style={{borderColor:"var(--green-60)", background:"var(--green-10)"}}>
              <div style={{padding:"16px"}}>
                <div className="row gap-2 mb-2"><Icon name="send" color="var(--green-60)"/><strong className="small">{lang==="id"?"Sudah terima kode sertifikat?":"Got your certificate code?"}</strong></div>
                <p className="small" style={{margin:0, color: "var(--green-60)"}}>
                  {lang==="id"
                    ? "Cek email dari admin, lalu masukkan kodenya di sini untuk mengunduh sertifikat PDF Anda."
                    : "Check your email from admin, then enter the code here to download your PDF certificate."}
                </p>
                <button className="btn sm mt-3" style={{background:"var(--success-fill)"}} onClick={()=>navigate("claim")}>
                  {lang==="id"?"Masukkan kode":"Enter code"}<Icon name="arrow_right" size={12} className="btn-icon"/>
                </button>
              </div>
            </div>

            <div className="tile" style={{borderColor:"var(--blue-60)", background:"var(--blue-10)"}}>
              <div style={{padding:"16px"}}>
                <div className="row gap-2 mb-2"><Icon name="qr" color="var(--blue-70)"/><strong className="small">{t.verifyCert}</strong></div>
                <p className="small" style={{margin:0, color: "var(--blue-80)"}}>
                  {lang==="id"
                    ? "Verifikasi keaslian sertifikat siapa pun via halaman publik atau scan QR."
                    : "Verify the authenticity of any certificate via the public page or by scanning the QR."}
                </p>
                <button className="btn sm mt-3" onClick={()=>setTab("verify")}>
                  {lang==="id"?"Buka verifikasi":"Open verifier"}<Icon name="arrow_right" size={12} className="btn-icon"/>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "verify" && (
        <div className="grid" style={{gridTemplateColumns: isMobile ? "1fr" : "1fr 1.4fr", gap: 24}}>
          <div className="tile tile-pad">
            <h3 className="h3 mb-3">{t.verifyCert}</h3>
            <p className="small muted mb-4">{lang==="id"?"Masukkan nomor sertifikat untuk memverifikasi.":"Enter the certificate number to verify."}</p>
            <div className="field mb-3">
              <label>{t.certNumber}</label>
              <input value={selectedCertNo} onChange={(e)=>setSelectedCertNo(e.target.value)} placeholder="LEMBAGA/PROGRAM/2026/..."/>
            </div>
            <div className="row gap-2">
              <button className="btn" onClick={handleVerify} disabled={verifying || !selectedCertNo.trim()}>
                <Icon name="check" size={14}/>
                {verifying ? (lang==="id"?"Memverifikasi…":"Verifying…") : (lang==="id"?"Verifikasi":"Verify")}
              </button>
            </div>
            <div className="divider"/>
            <div className="tiny muted">{lang==="id"?"Endpoint publik (tanpa login):":"Public endpoint (no login):"}</div>
            <div className="mono tiny mt-2" style={{padding:"8px 12px", background:"var(--g-10)", border:"1px solid var(--border)"}}>
              GET /api/v1/verify/{`{certNo}`}
            </div>
          </div>

          <div className="tile">
            <div className="tile-header">
              <h3>{lang==="id"?"Hasil verifikasi":"Verification result"}</h3>
              {verifyResult?.valid && <span className="tag green"><Icon name="check" size={10}/>{t.valid}</span>}
              {verifyResult && !verifyResult.valid && <span className="tag red"><Icon name="close" size={10}/>{lang==="id"?"TIDAK VALID":"INVALID"}</span>}
            </div>
            <div style={{padding:"24px"}}>
              {!verifyResult && (
                <p className="muted small" style={{textAlign:"center", padding:40}}>
                  {lang==="id"?"Masukkan nomor sertifikat lalu klik Verifikasi.":"Enter a certificate number, then click Verify."}
                </p>
              )}
              {verifyResult?.valid && (
                <>
                  <div className="row gap-4 mb-4">
                    <div style={{padding: 16, background:"white", border:"1px solid var(--border)"}}>
                      <QRCodeBlock token={verifyResult.certNo}/>
                    </div>
                    <div style={{flex:1}}>
                      <div className="tiny muted">{t.issuedTo}</div>
                      <div className="h2 mt-1">{verifyResult.participantName}</div>
                      <div className="muted small">{verifyResult.programName}</div>
                      <div className="divider"/>
                      <div className="grid grid-2" style={{gap:12}}>
                        <div><div className="tiny muted">{t.certNumber}</div><div className="mono small mt-1">{verifyResult.certNo}</div></div>
                        <div><div className="tiny muted">{lang==="id"?"Program":"Program"}</div><div className="small mt-1">{verifyResult.programName}</div></div>
                        <div><div className="tiny muted">{t.issuedOn}</div><div className="small mt-1 mono">{new Date(verifyResult.issuedAt).toLocaleDateString(lang==="id"?"id-ID":"en-US")}</div></div>
                        <div><div className="tiny muted">{t.issuedBy}</div><div className="small mt-1">{verifyResult.issuedBy || "—"}</div></div>
                      </div>
                    </div>
                  </div>
                  <div className="banner green">
                    <Icon name="check" color="var(--green-60)"/>
                    <div style={{flex:1}}>
                      <strong>{t.valid}.</strong> {lang==="id"?`Sertifikat resmi diterbitkan oleh ${verifyResult.issuedBy || "lembaga penerbit"}.`:`Officially issued by ${verifyResult.issuedBy || "the issuing institution"}.`}
                    </div>
                  </div>
                </>
              )}
              {verifyResult && !verifyResult.valid && (
                <div className="banner" style={{background:"var(--red-10)", borderColor:"var(--red-60)"}}>
                  <Icon name="alert" color="var(--red-60)"/>
                  <div style={{flex:1, color:"var(--red-60)"}}>
                    <strong>{lang==="id"?"Tidak valid.":"Invalid."}</strong> {verifyResult.error || (lang==="id"?"Sertifikat tidak ditemukan atau belum diklaim.":"Certificate not found or not yet claimed.")}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Visual certificate
/**
 * Apa yang terjadi pada sertifikat program ini setelah diterbitkan.
 *
 * Sampai sekarang sebuah lembaga tidak punya satu pun angka untuk menjawab itu:
 * sertifikat dikirim, lalu senyap. Layar ini yang menyediakannya — dan angka
 * yang paling berarti bukan "berapa yang terbit", melainkan berapa yang
 * benar-benar DIPAKAI: dibuka, dibagikan, dan diperiksa keasliannya oleh orang
 * lain.
 */
function CredentialAnalytics({ lang, programId, isMobile }) {
  const id = lang === "id";
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  // 90 hari: cukup panjang untuk memuat satu angkatan pelatihan sampai
  // sertifikatnya selesai beredar, cukup pendek supaya grafiknya masih terbaca.
  const [days, setDays] = React.useState(90);

  React.useEffect(() => {
    if (!programId) return;
    let batal = false;
    setLoading(true);
    setError("");
    const from = new Date(Date.now() - days * 864e5).toISOString().slice(0, 10);
    certAnalytics(programId, { from })
      .then((d) => { if (!batal) setData(d); })
      .catch((e) => { if (!batal) setError(e?.response?.data?.error || e.message); })
      .finally(() => { if (!batal) setLoading(false); });
    return () => { batal = true; };
  }, [programId, days]);

  if (!programId) {
    return <div className="tile tile-pad muted small">{id ? "Pilih program dulu." : "Select a program first."}</div>;
  }
  if (loading) {
    return <div className="tile tile-pad muted small">{id ? "Memuat…" : "Loading…"}</div>;
  }
  if (error) {
    return <div className="tile tile-pad small" style={{ color: "var(--red-60)" }}>{error}</div>;
  }

  const t = data?.totals || {};
  const kartu = [
    { label: id ? "Halaman dibuka" : "Page views", value: t.PAGE_VIEW || 0,
      hint: id ? "Kunjungan manusia, sudah dikurangi pemuatan ulang & mesin pratinjau" : "Human visits, de-duplicated and excluding preview bots" },
    { label: id ? "Diverifikasi" : "Verifications", value: t.VERIFY_LOOKUP || 0,
      hint: id ? "Orang luar memeriksa keaslian sertifikat" : "Outsiders checking authenticity" },
    { label: id ? "Dibagikan" : "Shares", value: t.SHARE_CLICK || 0,
      hint: id ? "Klik tombol bagikan di halaman kredensial" : "Share button clicks on the credential page" },
    { label: id ? "Diunduh" : "Downloads", value: (t.DOWNLOAD_PDF || 0) + (t.DOWNLOAD_PNG || 0),
      hint: id ? "PDF dan gambar" : "PDF and image" },
  ];

  // Deret waktu hanya menampilkan hari yang benar-benar ada peristiwanya —
  // program yang sepi tidak perlu grafik berisi sembilan puluh batang nol.
  const deret = (data?.timeseries || []).slice(-30);
  const grafik = deret.map((d) => ({
    label: new Date(d.date).toLocaleDateString(id ? "id-ID" : "en-US", { day: "2-digit", month: "short" }),
    value: d.PAGE_VIEW,
  }));

  const namaJaringan = {
    linkedin: "LinkedIn", linkedin_add: id ? "LinkedIn (profil)" : "LinkedIn (profile)",
    whatsapp: "WhatsApp", x: "X", facebook: "Facebook", copy: id ? "Salin tautan" : "Copy link",
  };

  return (
    <div className="col gap-4">
      <div className="row between" style={{ flexWrap: "wrap", gap: 12 }}>
        <div className="muted small">
          {id
            ? `${data.claimedCount} dari ${data.issuedCount} sertifikat sudah diambil pemiliknya.`
            : `${data.claimedCount} of ${data.issuedCount} certificates have been claimed.`}
        </div>
        <div className="row gap-2">
          {[30, 90, 365].map((d) => (
            <Btn key={d} variant={days === d ? "primary" : "secondary"} onClick={() => setDays(d)}>
              {d === 365 ? (id ? "1 tahun" : "1 year") : `${d} ${id ? "hari" : "days"}`}
            </Btn>
          ))}
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 16 }}>
        {kartu.map((k) => (
          <div key={k.label} className="tile tile-pad">
            <div className="tiny muted" style={{ textTransform: "uppercase", letterSpacing: ".08em" }}>{k.label}</div>
            <div className="mono" style={{ fontSize: 30, fontWeight: 300, marginTop: 6 }}>{k.value}</div>
            <div className="tiny muted mt-1" style={{ lineHeight: 1.4 }}>{k.hint}</div>
          </div>
        ))}
      </div>

      <div className="tile">
        <div className="tile-header">
          <h3>{id ? "Kunjungan per hari" : "Visits per day"}</h3>
          <span className="tile-header-sub small">{id ? "30 hari terakhir yang ada aktivitas" : "last 30 active days"}</span>
        </div>
        <div className="tile-pad">
          {grafik.length
            ? <BarChart data={grafik} height={180} />
            : <div className="muted small" style={{ textAlign: "center", padding: 24 }}>
                {id ? "Belum ada kunjungan pada rentang ini." : "No visits in this range yet."}
              </div>}
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: isMobile ? "1fr" : "1fr 1.4fr", gap: 16 }}>
        <div className="tile">
          <div className="tile-header"><h3>{id ? "Dibagikan ke mana" : "Shared where"}</h3></div>
          {(data.byNetwork || []).length === 0 && (
            <div className="tile-pad muted small" style={{ textAlign: "center" }}>—</div>
          )}
          {(data.byNetwork || []).map((n, i) => (
            <div key={n.network} className="row between" style={{ padding: "12px 16px", borderTop: i > 0 ? "1px solid var(--border)" : "none" }}>
              <span className="small">{namaJaringan[n.network] || n.network}</span>
              <span className="mono small">{n.count}</span>
            </div>
          ))}
        </div>

        <div className="tile">
          <div className="tile-header">
            <h3>{id ? "Kredensial paling banyak dilihat" : "Most viewed credentials"}</h3>
          </div>
          {(data.top || []).length === 0 && (
            <div className="tile-pad muted small" style={{ textAlign: "center" }}>—</div>
          )}
          {(data.top || []).map((c, i) => (
            <div key={c.certificateId} className="row between gap-3"
                 style={{ padding: "12px 16px", borderTop: i > 0 ? "1px solid var(--border)" : "none" }}>
              <div style={{ minWidth: 0 }}>
                <div className="small" style={{ fontWeight: 500 }}>{c.recipientName || "—"}</div>
                <div className="tiny muted mono">{c.certNo}</div>
              </div>
              <div className="row gap-3" style={{ flexShrink: 0 }}>
                <span className="tiny muted">{c.PAGE_VIEW} {id ? "dibuka" : "views"}</span>
                <span className="tiny muted">{c.SHARE_CLICK} {id ? "dibagikan" : "shares"}</span>
                {c.publicId && (
                  <a className="btn sm ghost icon-only" href={`/c/${c.publicId}`} target="_blank" rel="noopener noreferrer"
                     title={id ? "Halaman kredensial" : "Credential page"}>
                    <Icon name="link" size={12} />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="tiny muted" style={{ lineHeight: 1.6 }}>
        {id
          ? "Angka-angka ini anonim. Yang tersimpan hanya sidik ringkas dari alamat pengunjung yang berganti setiap hari — cukup untuk tidak menghitung satu orang dua kali, tidak cukup untuk mengetahui siapa dia."
          : "These figures are anonymous. Only a short daily-rotating fingerprint of each visitor is stored — enough not to count one person twice, not enough to identify them."}
      </div>
    </div>
  );
}

// Gambar dirender backend dari template program yang sama dengan PDF-nya.
function GambarSertifikat({ cert, lang }) {
  const id = lang === "id";
  const [status, setStatus] = React.useState("memuat");

  if (!cert.claimed_at || !cert.public_id) {
    return (
      <div className="tile tile-pad" style={{ aspectRatio: "297/210", display: "grid", placeItems: "center", textAlign: "center" }}>
        <div>
          <Icon name="cert" size={40} color="var(--text-tertiary)" />
          <p className="small muted mt-2">
            {id ? "Klaim sertifikat ini terlebih dahulu untuk melihat dokumennya." : "Claim this certificate first to see the document."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "relative", aspectRatio: "297/210", border: "1px solid var(--border)", background: "var(--g-10)", overflow: "hidden" }}>
      {status !== "siap" && (
        <div className="small muted" style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", textAlign: "center", padding: 24 }}>
          {status === "gagal"
            ? (id ? "Gambar sertifikat belum bisa ditampilkan. Gunakan tombol Download PDF." : "The certificate image is unavailable. Use Download PDF instead.")
            : (id ? "Menyiapkan gambar sertifikat…" : "Preparing certificate image…")}
        </div>
      )}
      <img
        src={`/c/${cert.public_id}/image.png`}
        alt={`${id ? "Sertifikat" : "Certificate"} ${cert.cert_no}`}
        onLoad={() => setStatus("siap")}
        onError={() => setStatus("gagal")}
        style={{ width: "100%", height: "100%", objectFit: "contain", display: "block", opacity: status === "siap" ? 1 : 0 }}
      />
    </div>
  );
}

// =============================================================
// PROGRESS / ANALYTICS
// =============================================================
export const RANGE_OPTIONS = [
  { value: "7d", id: "7 hari terakhir", en: "Last 7 days" },
  { value: "30d", id: "30 hari terakhir", en: "Last 30 days" },
  { value: "quarter", id: "Kuartal ini", en: "This quarter" },
];

const LEVEL_NAMES = {
  1: "First Responder",
  2: "On-Scene Commander",
  3: "Senior Management",
};

// Client-side CSV of the overview — keeps the Export button real without
// a second backend round-trip.
export function exportOverviewCsv(data, lang, orgName) {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = [
    [`${orgName} — Analytics`],
    [lang === "id" ? "Dibuat" : "Generated", new Date().toISOString()],
    [lang === "id" ? "Program" : "Programs", data.programCount],
    [],
    [lang === "id" ? "Metrik" : "Metric", lang === "id" ? "Nilai" : "Value"],
    [lang === "id" ? "Total watch-time (jam)" : "Total watch-time (h)", data.stats.watchHours],
    [lang === "id" ? "Tingkat penyelesaian (%)" : "Completion rate (%)", data.stats.completionRatePct],
    [lang === "id" ? "Tingkat drop-off (%)" : "Drop-off rate (%)", data.stats.dropOffRatePct],
    [lang === "id" ? "Peserta" : "Participants", data.stats.participants],
    [],
    [lang === "id" ? "Progres per level" : "Progress by level"],
    ["Level", lang === "id" ? "Peserta" : "Participants", lang === "id" ? "% Selesai" : "Completion %"],
    ...data.byLevel.map((l) => [`L${l.level}`, l.participants, l.completionPct]),
    [],
    [lang === "id" ? "Pelajaran drop-off tertinggi" : "Highest drop-off lessons"],
    [lang === "id" ? "Pelajaran" : "Lesson", lang === "id" ? "Modul" : "Module",
      lang === "id" ? "Memulai" : "Started", "Drop-off %"],
    ...data.dropOff.map((d) => [d.title, d.moduleTitle, d.startedCount, d.dropOffPct]),
  ];
  const csv = "﻿" + rows.map((r) => r.map(esc).join(",")).join("\r\n") + "\r\n";
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `platform-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function ProgressAnalyticsScreen() {
  const orgName = useOrgName()
  const t = useT();
  const { lang, role, navigate } = useApp();
  const isMobile = useIsMobile();

  const isTrainer = role === "trainer";
  const [range, setRange] = React.useState("30d");
  const [reloadTick, setReloadTick] = React.useState(0);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    analyticsOverview(range)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        toast.error(lang === "id" ? "Gagal memuat analitik" : "Failed to load analytics");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range, reloadTick, lang]);

  const heatmap = data?.heatmap;
  const moduleCols = heatmap?.moduleCount || 0;
  const maxDrop = Math.max(1, ...(data?.dropOff || []).map((d) => d.dropOffPct));
  const timeline = data?.timeline || [];

  return (
    <div className="main-inner">
      <div className="page-header">
        <div className="page-header-text">
          <div className="eyebrow">{orgName} · {lang === "id" ? "Analytics" : "Analytics"}</div>
          <h1 className="h1" style={{ marginTop: 6 }}>
            {isTrainer ? (lang==="id"?"Analitik Kelas":"Class Analytics") : (lang==="id"?"Analitik Platform":"Platform Analytics")}
          </h1>
          <div className="muted mt-1">
            {isTrainer
              ? (lang==="id"?"Metrik agregat lintas program yang Anda ampu.":"Aggregate metrics across the programs you manage.")
              : (lang==="id"?"Metrik agregat lintas seluruh program dan level.":"Aggregate metrics across all programs and levels.")
            }
          </div>
        </div>
        <div className="page-header-actions">
          <select
            value={range}
            onChange={(e)=>setRange(e.target.value)}
            style={{height:32, padding:"0 8px", background:"var(--surface)", border:"1px solid var(--border-strong)", fontSize:13}}
          >
            {RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{lang==="id"?o.id:o.en}</option>
            ))}
          </select>
          <Btn variant="secondary" icon={<Icon name="download" size={14}/>}
            disabled={!data || loading}
            onClick={()=>data && exportOverviewCsv(data, lang, orgName)}>
            {lang==="id"?"Ekspor":"Export"}
          </Btn>
        </div>
      </div>

      {loading && (
        <div aria-busy="true" aria-label={lang==="id"?"Memuat analitik":"Loading analytics"}>
          <div className="grid grid-4 mb-5">
            {[0,1,2,3].map((i)=>(
              <div className="stat-card" key={i}>
                <div className="chart-skeleton" style={{height:12, width:"55%"}}/>
                <div className="chart-skeleton" style={{height:26, width:"40%", marginTop:10}}/>
              </div>
            ))}
          </div>
          <div className="grid" style={{gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr", gap:16}}>
            {[220, 220].map((h,i)=>(
              <div className="tile tile-pad" key={i}>
                <div className="chart-skeleton" style={{height:h}}/>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && error && (
        <div className="tile tile-pad" style={{textAlign:"center", padding:60}}>
          <Icon name="alert" size={40} color="var(--text-tertiary)"/>
          <h3 className="h3 mt-3">{lang==="id"?"Gagal memuat data":"Couldn't load data"}</h3>
          <button className="btn mt-3" onClick={()=>setReloadTick((n)=>n+1)}>
            {lang==="id"?"Coba lagi":"Retry"}
          </button>
        </div>
      )}

      {!loading && !error && data && data.programCount === 0 && (
        <div className="tile tile-pad muted small" style={{textAlign:"center", padding:60}}>
          <Icon name="chart" size={40} color="var(--text-tertiary)"/>
          <h3 className="h3 mt-3" style={{color:"var(--text)"}}>{lang==="id"?"Belum ada program":"No programs yet"}</h3>
          <p className="mt-2">{lang==="id"?"Analitik akan muncul setelah ada program dengan aktivitas peserta.":"Analytics appear once a program has participant activity."}</p>
        </div>
      )}

      {!loading && !error && data && data.programCount > 0 && (
        <>
          {/* Stat row */}
          <div className="grid grid-4 mb-5">
            <Stat
              label={lang==="id"?"Total watch-time":"Total watch-time"}
              value={`${fmtNum(data.stats.watchHours, lang)} ${lang==="id"?"jam":"h"}`}
              hint={`${data.programCount} ${lang==="id"?"program":"programs"}`}
            />
            <Stat
              label={t.completionRate}
              value={`${data.stats.completionRatePct}%`}
            />
            <Stat
              label={t.dropOffRate}
              value={`${data.stats.dropOffRatePct}%`}
              hint={lang==="id"?"Lebih rendah lebih baik":"Lower is better"}
            />
            <Stat
              label={lang==="id"?"Peserta":"Participants"}
              value={fmtNum(data.stats.participants, lang)}
              hint={lang==="id"?"Peserta unik terdaftar":"Unique enrolled participants"}
            />
          </div>

          <div className="grid" style={{gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr", gap: 16}}>
            {/* Watch-time heatmap — ramp satu-hue; sel dan legend membaca
                token --seq-b-* yang sama, jadi tidak bisa lagi tidak cocok. */}
            <div className="tile">
              <div className="tile-header">
                <h3>{lang==="id"?"Watch-time per modul (heatmap)":"Watch-time per module (heatmap)"}</h3>
                <span className="tile-header-sub">{lang==="id"?"rata-rata %":"avg %"}</span>
              </div>
              <div style={{padding:"16px 20px"}}>
                {moduleCols === 0 ? (
                  <div className="chart-empty">
                    {lang==="id"?"Belum ada data watch-time.":"No watch-time data yet."}
                  </div>
                ) : (
                  <Heatmap
                    lang={lang}
                    ariaLabel={lang==="id"?"Watch-time rata-rata per modul, per program":"Average watch-time per module, per program"}
                    rowHeader="Program"
                    unitLabel={lang==="id"?"Rata-rata":"Average"}
                    columns={Array.from({length:moduleCols}).map((_,i)=>`M${i+1}`)}
                    rows={heatmap.rows.map((row)=>({
                      key: row.programId,
                      label: `L${row.level} · ${row.code || row.name}`,
                      cells: row.cells,
                    }))}
                  />
                )}
              </div>
            </div>

            {/* Drop-off — satu seri, warna semantik (buruk), jadi tetap
                --danger dan tanpa legend. */}
            <div className="tile">
              <div className="tile-header"><h3>{t.dropOffRate}</h3></div>
              <div style={{padding: "16px 20px"}}>
                <div className="small muted mb-3">{lang==="id"?"Pelajaran video dengan drop-off tertinggi":"Video lessons with highest drop-off"}</div>
                <HBarChart
                  lang={lang}
                  color="var(--danger)"
                  max={maxDrop}
                  valueSuffix="%"
                  emptyText={lang==="id"?"Belum ada data.":"No data yet."}
                  data={data.dropOff.map((l)=>({
                    key: l.lessonId,
                    label: l.title,
                    title: `${l.moduleTitle} · ${l.title}`,
                    value: l.dropOffPct,
                    sub: `${l.moduleTitle} · ${l.startedCount} ${lang==="id"?"penonton":"viewers"}`,
                  }))}
                />
              </div>
            </div>
          </div>

          {/* Lower row */}
          <div className="grid mt-4" style={{gridTemplateColumns: isMobile ? "1fr" : "1fr 2fr", gap: 16}}>
            {/* By level — memakai LEVEL_COLORS, bukan palet kategoris:
                LevelBadge di baris yang sama sudah terikat warna itu. */}
            <div className="tile">
              <div className="tile-header"><h3>{t.progressByLevel}</h3></div>
              <div style={{padding:"16px 20px"}}>
                {data.byLevel.length === 0 && (
                  <div className="chart-empty">
                    {lang==="id"?"Belum ada data.":"No data yet."}
                  </div>
                )}
                {data.byLevel.map((x)=>(
                  <div key={x.level} className="mb-4">
                    <div className="row between mb-2">
                      <div className="row gap-2"><LevelBadge level={x.level}/><span className="small">{x.level == null ? (lang === "id" ? "Tanpa jenjang" : "No tier") : (LEVEL_NAMES[x.level] || `Level ${x.level}`)}</span></div>
                      <span className="mono tnum small">{x.completionPct}%</span>
                    </div>
                    <Progress
                      value={x.completionPct}
                      color={LEVEL_COLORS[x.level] || "var(--text-3)"}
                      style={{height:8}}
                    />
                    <div className="tiny muted mt-1 mono">{x.participants} {lang==="id"?"peserta":"participants"}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Engagement timeline */}
            <div className="tile">
              <div className="tile-header">
                <h3>{lang==="id"?`Aktivitas harian · ${data.range.days} hari`:`Daily activity · ${data.range.days} days`}</h3>
              </div>
              <div style={{padding: "16px 20px"}}>
                {timeline.length === 0 ? (
                  <div className="chart-empty">{t.chartNoData}</div>
                ) : (
                  <LineChart
                    lang={lang}
                    height={200}
                    allowTable
                    ariaLabel={lang==="id"?`Aktivitas harian selama ${data.range.days} hari`:`Daily activity over ${data.range.days} days`}
                    formatX={(iso)=>fmtDateShort(iso, lang)}
                    labels={{
                      chart: t.chartViewChart,
                      table: t.chartViewTable,
                      x: t.chartDate,
                    }}
                    series={[
                      { name: t.chartWatchSessions, points: timeline.map((p)=>({x:p.date, y:p.watch})) },
                      { name: t.chartTests, points: timeline.map((p)=>({x:p.date, y:p.test})) },
                    ]}
                  />
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const DEFAULT_CANVAS_DATA = {
  fields: {
    participantName: { top: 50, left: 50, fontSize: 32, fontStyle: "italic", fontWeight: "bold", color: "#0F172A", align: "center", visible: true },
    certNo: { top: 10, left: 90, fontSize: 12, fontStyle: "normal", fontWeight: "normal", color: "#64748B", align: "right", visible: true },
    validUntil: { top: 16, left: 90, fontSize: 12, fontStyle: "normal", fontWeight: "normal", color: "#64748B", align: "right", visible: true },
    programName: { top: 60, left: 50, fontSize: 20, fontStyle: "normal", fontWeight: "bold", color: "#0F172A", align: "center", visible: true },
    oprcLevel: { top: 66, left: 50, fontSize: 14, fontStyle: "normal", fontWeight: "normal", color: "#475569", align: "center", visible: true },
    issueDate: { top: 72, left: 50, fontSize: 12, fontStyle: "normal", fontWeight: "normal", color: "#475569", align: "center", visible: true },
    company: { top: 56, left: 50, fontSize: 14, fontStyle: "normal", fontWeight: "normal", color: "#475569", align: "center", visible: true },
    nauticalCertNo: { top: 22, left: 90, fontSize: 12, fontStyle: "normal", fontWeight: "normal", color: "#64748B", align: "right", visible: false },
    placeDateOfBirth: { top: 44, left: 50, fontSize: 12, fontStyle: "normal", fontWeight: "normal", color: "#475569", align: "center", visible: false },
    trainingStartDate: { top: 76, left: 35, fontSize: 12, fontStyle: "normal", fontWeight: "normal", color: "#475569", align: "center", visible: false },
    trainingEndDate: { top: 76, left: 55, fontSize: 12, fontStyle: "normal", fontWeight: "normal", color: "#475569", align: "center", visible: false },
    trainingLocation: { top: 76, left: 75, fontSize: 12, fontStyle: "normal", fontWeight: "normal", color: "#475569", align: "center", visible: false },
    qrCode: { top: 82, left: 88, size: 60, visible: true }
  },
  signatures: [
    // Dulu terisi "Capt. Rina Wijaya — IMO Certified Instructor": nama orang rekaan yang
    // ikut tercetak di sertifikat sungguhan bila admin menyimpan templat tanpa
    // mengubahnya. Kosong di sini; editor menampilkan "[Nama Penandatangan]" dan
    // "[Jabatan]" sebagai petunjuk, dan keduanya tidak ikut tersimpan.
    { id: "sig-1", top: 82, left: 20, imageUrl: "", name: "", title: "", visible: true },
    { id: "sig-2", top: 82, left: 50, imageUrl: "", name: "", title: "", visible: false },
    { id: "sig-3", top: 82, left: 75, imageUrl: "", name: "", title: "", visible: false }
  ],
  customTexts: []
};

// Salinan lepas dari DEFAULT_CANVAS_DATA, bukan objek konstanta itu sendiri.
// Lapis pertahanan kedua: perbaikan di atas membuat setiap handler styling
// membangun objek `fields` baru, jadi konstanta ini seharusnya sudah tidak
// termutasi lagi — tapi kalau ada kode baru nanti yang lupa pola itu (mis.
// mutasi di tempat pada elemen bertipe lain), akibatnya cuma mengotori satu
// salinan sekali pakai, bukan default yang dipakai bersama semua program.
function defaultCanvasData() {
  return structuredClone(DEFAULT_CANVAS_DATA);
}

// Template yang tersimpan sebelum sebuah field ditambahkan tidak memuat kuncinya,
// dan Daftar Elemen hanya mengulang kunci yang ada — tanpa ini field baru tak
// pernah muncul untuk template lama. Field yang dilengkapi selalu tersembunyi:
// sertifikat yang sudah didesain tidak boleh mendadak memuat elemen yang tidak
// pernah dipilih admin. Tanda tangan dan teks bebas dibiarkan apa adanya.
function lengkapiCanvas(saved) {
  const tersimpan = saved.fields || {};
  const tambahan = Object.fromEntries(
    Object.entries(defaultCanvasData().fields)
      .filter(([key]) => !(key in tersimpan))
      .map(([key, field]) => [key, { ...field, visible: false }])
  );
  return { ...saved, fields: { ...tersimpan, ...tambahan } };
}

function TemplateDesigner({ lang, currentProgramId, currentProgram }) {
  const isMobile = useIsMobile();
  const [bgImageUrl, setBgImageUrl] = React.useState("");
  const [canvasData, setCanvasData] = React.useState(defaultCanvasData);
  const [selectedElement, setSelectedElement] = React.useState(null); // { key, isSignature, sigIndex }
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [uploadingBg, setUploadingBg] = React.useState(false);
  const [uploadingSig, setUploadingSig] = React.useState(false);

  const canvasRef = React.useRef(null);
  const [canvasWidth, setCanvasWidth] = React.useState(800);

  // Editor's canvas is fluid (width:100% of a flexible grid column), but every
  // downstream renderer — the participant-facing preview (ClaimScreens.jsx)
  // and the Puppeteer PDF (cert.service.js) — treats stored px values (font
  // size, QR size, signature height) as if they were sized on an 800px-wide
  // canvas. Without this, an admin designing on a wide desktop monitor picks
  // sizes that look right there but end up wrong everywhere the design is
  // actually rendered. Scaling the on-screen preview by canvasWidth/800 makes
  // the editor a true WYSIWYG match for both downstream renderers.
  React.useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      if (!entries || !entries[0]) return;
      setCanvasWidth(entries[0].contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, currentProgramId]);

  const editorScale = canvasWidth / 800;

  React.useEffect(() => {
    if (!currentProgramId) return;
    setLoading(true);
    getCertificateTemplate(currentProgramId)
      .then(data => {
        if (data) {
          setBgImageUrl(data.bg_image_url || "");
          setCanvasData(data.canvas_data ? lengkapiCanvas(data.canvas_data) : defaultCanvasData());
        } else {
          setBgImageUrl("");
          setCanvasData(defaultCanvasData());
        }
      })
      .catch(() => {
        setBgImageUrl("");
        setCanvasData(defaultCanvasData());
      })
      .finally(() => setLoading(false));
  }, [currentProgramId]);

  if (!currentProgramId) {
    return (
      <div className="tile tile-pad animate-fade-in" style={{ textAlign: "center", padding: 60 }}>
        <Icon name="alert" size={48} color="var(--text-tertiary)" />
        <h3 className="h3 mt-3">{lang === "id" ? "Pilih Program Terlebih Dahulu" : "Select a Program First"}</h3>
        <p className="muted small mt-2">
          {lang === "id"
            ? "Silakan pilih program aktif di sidebar untuk mengedit template sertifikatnya."
            : "Please select an active program in the sidebar to edit its certificate template."}
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="tile tile-pad" style={{ textAlign: "center", padding: 60 }}>
        <p className="small muted">{lang === "id" ? "Memuat template..." : "Loading template..."}</p>
      </div>
    );
  }

  const handleMouseDown = (key, e, isSignature = false, sigIndex = null, isCustomText = false, customTextIndex = null) => {
    e.preventDefault();
    const canvasElement = canvasRef.current;
    if (!canvasElement) return;

    const rect = canvasElement.getBoundingClientRect();
    setSelectedElement({ key, isSignature, sigIndex, isCustomText, customTextIndex });

    const handleMouseMove = (moveEvent) => {
      let left = ((moveEvent.clientX - rect.left) / rect.width) * 100;
      let top = ((moveEvent.clientY - rect.top) / rect.height) * 100;

      left = Math.max(0, Math.min(100, left));
      top = Math.max(0, Math.min(100, top));

      left = Math.round(left * 10) / 10;
      top = Math.round(top * 10) / 10;

      setCanvasData(prev => {
        const next = { ...prev };
        if (isCustomText) {
          next.customTexts = prev.customTexts.map((ct, i) =>
            i === customTextIndex ? { ...ct, left, top } : ct
          );
        } else if (isSignature) {
          next.signatures = prev.signatures.map((sig, idx) =>
            idx === sigIndex ? { ...sig, left, top } : sig
          );
        } else {
          next.fields = {
            ...prev.fields,
            [key]: { ...prev.fields[key], left, top }
          };
        }
        return next;
      });
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleAddCustomText = () => {
    const id = `ct-${Date.now()}`;
    const newCt = { id, top: 50, left: 50, text: lang === "id" ? "Teks baru" : "New text", fontSize: 16, fontStyle: "normal", fontWeight: "normal", color: "#0F172A", align: "center", visible: true };
    const newIdx = (canvasData.customTexts?.length || 0);
    setCanvasData(prev => ({ ...prev, customTexts: [...(prev.customTexts || []), newCt] }));
    setSelectedElement({ key: id, isSignature: false, sigIndex: null, isCustomText: true, customTextIndex: newIdx });
  };

  const handleDeleteCustomText = (idx) => {
    setCanvasData(prev => ({ ...prev, customTexts: prev.customTexts.filter((_, i) => i !== idx) }));
    setSelectedElement(null);
  };

  const handleAddSignature = () => {
    const id = `sig-${Date.now()}`;
    const newSig = { id, top: 82, left: 50, imageUrl: "", name: "", title: "", visible: true };
    const newIdx = canvasData.signatures.length;
    setCanvasData(prev => ({ ...prev, signatures: [...prev.signatures, newSig] }));
    setSelectedElement({ key: id, isSignature: true, sigIndex: newIdx, isCustomText: false, customTextIndex: null });
  };

  const handleDeleteSignature = (idx) => {
    setCanvasData(prev => ({ ...prev, signatures: prev.signatures.filter((_, i) => i !== idx) }));
    setSelectedElement(null);
  };

  const handleBgUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBg(true);
    try {
      const res = await uploadCertificateAsset(currentProgramId, file);
      setBgImageUrl(res.url);
      toast.success(lang === "id" ? "Background sertifikat berhasil diunggah" : "Background certificate uploaded successfully");
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message);
    } finally {
      setUploadingBg(false);
    }
  };

  const handleSigUpload = async (idx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingSig(true);
    try {
      const res = await uploadCertificateAsset(currentProgramId, file);
      setCanvasData(prev => {
        const next = { ...prev };
        next.signatures = prev.signatures.map((sig, i) => 
          i === idx ? { ...sig, imageUrl: res.url } : sig
        );
        return next;
      });
      toast.success(lang === "id" ? "Tanda tangan berhasil diunggah" : "Signature uploaded successfully");
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message);
    } finally {
      setUploadingSig(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Tanpa gambar background state-nya "", yang ditolak validasi URL server.
      await updateCertificateTemplate(currentProgramId, { bgImageUrl: bgImageUrl || null, canvasData });
      toast.success(lang === "id" ? "Desain template berhasil disimpan" : "Template design saved successfully");
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm(lang === "id" ? "Reset posisi dan styling ke default?" : "Reset layout positions and styling to default?")) {
      setCanvasData(defaultCanvasData());
      setSelectedElement(null);
    }
  };

  const activeField = selectedElement && !selectedElement.isSignature && !selectedElement.isCustomText ? canvasData.fields[selectedElement.key] : null;
  const activeSig = selectedElement?.isSignature ? canvasData.signatures[selectedElement.sigIndex] : null;
  const activeCustomText = selectedElement?.isCustomText ? canvasData.customTexts?.[selectedElement.customTextIndex] : null;

  return (
    <div className="grid animate-fade-in" style={{ gridTemplateColumns: isMobile ? "1fr" : "1fr 320px", gap: 24, alignItems: "start", marginTop: 20 }}>
      {/* Left side: Canvas */}
      <div className="col gap-3">
        <div className="tile tile-pad" style={{ padding: 24 }}>
          <div className="row between mb-4" style={{ flexWrap: "wrap", gap: 12 }}>
            <div>
              <h3 className="h3" style={{ fontSize: 18 }}>{lang === "id" ? `Editor Visual Sertifikat` : `Visual Certificate Editor`}</h3>
              <div className="small muted mt-1">{lang === "id" ? "Drag & drop elemen di bawah untuk mengatur posisi relatif." : "Drag & drop elements below to arrange relative positions."}</div>
            </div>
            <div className="row gap-2">
              <button className="btn secondary sm" onClick={handleReset}>{lang === "id" ? "Reset Default" : "Reset Default"}</button>
              <button className="btn primary sm" onClick={handleSave} disabled={saving}>
                {saving ? (lang === "id" ? "Menyimpan..." : "Saving...") : (lang === "id" ? "Simpan Desain" : "Save Design")}
              </button>
            </div>
          </div>

          <div
            ref={canvasRef}
            style={{
              position: "relative",
              width: "100%",
              aspectRatio: "297/210",
              backgroundImage: bgImageUrl ? `url(${bgImageUrl})` : "linear-gradient(135deg, var(--program-color)11 0%, #fff 50%, var(--program-color)22 100%)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              border: bgImageUrl ? "1px solid var(--border)" : "2px dashed var(--border-strong)",
              borderRadius: "var(--r-md)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
              overflow: "hidden",
            }}
          >
            {!bgImageUrl && (
              <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "#64748B" }}>
                <div style={{ textAlign: "center" }}>
                  <Icon name="upload" size={32} />
                  <div className="small mt-2">{lang === "id" ? "Belum ada gambar background. Unggah di panel kanan." : "No background image. Upload in right panel."}</div>
                </div>
              </div>
            )}

            {/* Render Text Fields */}
            {Object.keys(canvasData.fields).map(key => {
              const field = canvasData.fields[key];
              if (!field || field.visible === false) return null;

              if (key === 'qrCode') {
                const size = (field.size || 60) * editorScale;
                const isSelected = selectedElement?.key === key;
                return (
                  <div
                    key={key}
                    onMouseDown={(e) => handleMouseDown(key, e)}
                    style={{
                      position: "absolute",
                      left: `${field.left}%`,
                      top: `${field.top}%`,
                      width: `${size}px`,
                      height: `${size}px`,
                      transform: "translate(-50%, -50%)",
                      border: isSelected ? "2px solid var(--blue-60)" : "1px dashed #64748B",
                      background: "rgba(255,255,255,0.9)",
                      cursor: "move",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: isSelected ? 10 : 2,
                    }}
                  >
                    <div style={{ fontSize: "8px", fontWeight: "bold", textAlign: "center", color: "#000" }}>QR CODE</div>
                  </div>
                );
              }

              const isSelected = selectedElement?.key === key;
              let transform = 'translate(-50%, -50%)';
              if (field.align === 'left') transform = 'translate(0%, -50%)';
              if (field.align === 'right') transform = 'translate(-100%, -50%)';

              const displayValues = {
                participantName: lang === "id" ? "[Nama Lengkap Peserta]" : "[Participant Full Name]",
                certNo: "LEMBAGA/PROGRAM/2026/XXXXXX",
                validUntil: new Date(new Date().setFullYear(new Date().getFullYear() + 5)).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }),
                programName: currentProgram?.name || "",
                oprcLevel: tierLabelOrEmpty(currentProgram),
                issueDate: new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }),
                company: lang === "id" ? "[Nama Perusahaan]" : "[Company Name]",
                nauticalCertNo: "NAUTICAL/2026/001",
                placeDateOfBirth: "Jakarta, 12 Juni 1995",
                trainingStartDate: "10 Februari 2026",
                trainingEndDate: "14 Februari 2026",
                trainingLocation: lang === "id" ? "[Lokasi Pelatihan]" : "[Training Location]"
              };

              return (
                <div
                  key={key}
                  onMouseDown={(e) => handleMouseDown(key, e)}
                  style={{
                    position: "absolute",
                    left: `${field.left}%`,
                    top: `${field.top}%`,
                    transform: transform,
                    fontSize: `${(field.fontSize || 14) * editorScale}px`,
                    fontFamily: "'Georgia', 'Arial', serif",
                    fontStyle: field.fontStyle || "normal",
                    fontWeight: field.fontWeight || "normal",
                    textAlign: field.align || "center",
                    cursor: "move",
                    padding: "2px 6px",
                    border: isSelected ? "2px solid var(--blue-60)" : bgImageUrl ? "1px dashed rgba(255,255,255,0.7)" : "1px dashed transparent",
                    background: isSelected ? "rgba(15,98,254,0.1)" : bgImageUrl ? "rgba(0,0,0,0.18)" : "transparent",
                    color: isSelected ? (field.color || "#0F172A") : bgImageUrl ? "white" : (field.color || "#0F172A"),
                    textShadow: bgImageUrl && !isSelected ? "0 1px 3px rgba(0,0,0,0.8)" : "none",
                    whiteSpace: "nowrap",
                    userSelect: "none",
                    zIndex: isSelected ? 10 : 2,
                  }}
                >
                  {displayValues[key] || key}
                </div>
              );
            })}

            {/* Render Custom Texts */}
            {(canvasData.customTexts || []).map((ct, idx) => {
              if (!ct || ct.visible === false) return null;
              const isSelected = selectedElement?.isCustomText && selectedElement?.customTextIndex === idx;
              let transform = 'translate(-50%, -50%)';
              if (ct.align === 'left') transform = 'translate(0%, -50%)';
              if (ct.align === 'right') transform = 'translate(-100%, -50%)';
              return (
                <div
                  key={ct.id}
                  onMouseDown={(e) => handleMouseDown(ct.id, e, false, null, true, idx)}
                  style={{
                    position: "absolute",
                    left: `${ct.left}%`,
                    top: `${ct.top}%`,
                    transform,
                    fontSize: `${(ct.fontSize || 16) * editorScale}px`,
                    fontFamily: "'Georgia', 'Arial', serif",
                    fontStyle: ct.fontStyle || "normal",
                    fontWeight: ct.fontWeight || "normal",
                    color: isSelected ? (ct.color || "#0F172A") : bgImageUrl ? "white" : (ct.color || "#0F172A"),
                    textAlign: ct.align || "center",
                    cursor: "move",
                    padding: "2px 6px",
                    border: isSelected ? "2px solid var(--blue-60)" : bgImageUrl ? "1px dashed rgba(255,255,255,0.7)" : "1px dashed transparent",
                    background: isSelected ? "rgba(15,98,254,0.1)" : bgImageUrl ? "rgba(0,0,0,0.18)" : "transparent",
                    textShadow: bgImageUrl && !isSelected ? "0 1px 3px rgba(0,0,0,0.8)" : "none",
                    whiteSpace: "nowrap",
                    userSelect: "none",
                    zIndex: isSelected ? 10 : 2,
                  }}
                >
                  {ct.text || (lang === "id" ? "[Teks Kustom]" : "[Custom Text]")}
                </div>
              );
            })}

            {/* Render Signatures */}
            {canvasData.signatures.map((sig, idx) => {
              if (!sig || sig.visible === false) return null;
              const isSelected = selectedElement?.isSignature && selectedElement?.sigIndex === idx;

              return (
                <div
                  key={sig.id}
                  onMouseDown={(e) => handleMouseDown(sig.id, e, true, idx)}
                  style={{
                    position: "absolute",
                    left: `${sig.left}%`,
                    top: `${sig.top}%`,
                    transform: "translate(-50%, -50%)",
                    cursor: "move",
                    padding: "6px",
                    border: isSelected ? "2px solid var(--blue-60)" : "1px dashed #64748B",
                    background: "rgba(255,255,255,0.75)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    minWidth: "120px",
                    zIndex: isSelected ? 10 : 2,
                  }}
                >
                  {sig.imageUrl ? (
                    <img src={sig.imageUrl} alt="" style={{ maxHeight: `${30 * editorScale}px`, marginBottom: "4px", pointerEvents: "none" }} />
                  ) : (
                    <div style={{ height: `${30 * editorScale}px`, fontSize: "10px", display: "grid", placeItems: "center", color: "#64748B" }}>
                      {lang === "id" ? "[Ttd digital]" : "[Digital sig]"}
                    </div>
                  )}
                  <div style={{ fontSize: `${11 * editorScale}px`, fontFamily: "'Georgia', 'Arial', serif", fontWeight: "bold", textDecoration: "underline", color: "#000" }}>{sig.name || (lang === "id" ? "[Nama Penandatangan]" : "[Signee Name]")}</div>
                  <div style={{ fontSize: `${9 * editorScale}px`, fontFamily: "'Georgia', 'Arial', serif", color: "#475569" }}>{sig.title || (lang === "id" ? "[Jabatan]" : "[Title]")}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right side: Sidebar Control Panel */}
      <div className="col gap-3">
        {/* Background Uploader */}
        <div className="tile tile-pad" style={{ padding: 18 }}>
          <h4 className="h4 mb-3" style={{ fontSize: 14 }}>{lang === "id" ? "Background Sertifikat" : "Certificate Background"}</h4>
          <input
            type="file"
            accept="image/*"
            onChange={handleBgUpload}
            id="bg-file-input"
            style={{ display: "none" }}
          />
          <div className="row gap-2">
            <button
              className="btn secondary sm"
              style={{ flex: 1, height: 32 }}
              onClick={() => document.getElementById("bg-file-input").click()}
              disabled={uploadingBg}
            >
              {uploadingBg ? "..." : (lang === "id" ? "Unggah Background" : "Upload Background")}
            </button>
            {bgImageUrl && (
              <button className="btn sm outline red" style={{ height: 32 }} onClick={() => setBgImageUrl("")}>
                <Icon name="trash" size={14} />
              </button>
            )}
          </div>
          <div className="tiny muted mt-2" style={{ lineHeight: 1.3 }}>{lang === "id" ? "Rekomendasi: Gambar landscape A4 PNG/JPG (Maks 1MB)" : "Recommended: A4 landscape image PNG/JPG (Max 1MB)"}</div>
        </div>

        {/* Selected Element Styling Properties */}
        <div className="tile tile-pad" style={{ padding: 18 }}>
          <h4 className="h4 mb-3" style={{ fontSize: 14 }}>{lang === "id" ? "Pengaturan Elemen" : "Element Styling"}</h4>
          {!selectedElement ? (
            <p className="small muted" style={{ margin: 0 }}>{lang === "id" ? "Klik elemen pada sertifikat untuk mengatur posisinya dan gayanya." : "Click any element in the certificate template to style it."}</p>
          ) : (
            <div className="col gap-3">
              <div className="row between">
                <span className="small" style={{ fontWeight: 500 }}>{lang === "id" ? "Tampilkan elemen" : "Show element"}</span>
                <input
                  type="checkbox"
                  checked={
                    selectedElement.isCustomText
                      ? activeCustomText?.visible !== false
                      : selectedElement.isSignature
                        ? activeSig?.visible !== false
                        : activeField?.visible !== false
                  }
                  onChange={(e) => {
                    const visible = e.target.checked;
                    setCanvasData(prev => {
                      const next = { ...prev };
                      if (selectedElement.isCustomText) {
                        next.customTexts = prev.customTexts.map((ct, i) =>
                          i === selectedElement.customTextIndex ? { ...ct, visible } : ct
                        );
                      } else if (selectedElement.isSignature) {
                        next.signatures = prev.signatures.map((sig, i) =>
                          i === selectedElement.sigIndex ? { ...sig, visible } : sig
                        );
                      } else {
                        next.fields = {
                          ...prev.fields,
                          [selectedElement.key]: { ...prev.fields[selectedElement.key], visible }
                        };
                      }
                      return next;
                    });
                  }}
                />
              </div>

              {selectedElement.isCustomText && activeCustomText && (
                <>
                  <label className="col gap-1">
                    <span className="tiny muted">{lang === "id" ? "Isi Teks" : "Text Content"}</span>
                    <input
                      type="text"
                      value={activeCustomText.text || ""}
                      onChange={(e) => {
                        const text = e.target.value;
                        setCanvasData(prev => ({
                          ...prev,
                          customTexts: prev.customTexts.map((ct, i) =>
                            i === selectedElement.customTextIndex ? { ...ct, text } : ct
                          )
                        }));
                      }}
                      style={{ height: 32, padding: "0 8px", border: "1px solid var(--border-strong)", fontSize: 13 }}
                    />
                  </label>

                  <label className="col gap-1">
                    <span className="tiny muted">{lang === "id" ? "Ukuran Huruf (px)" : "Font Size (px)"}</span>
                    <input
                      type="number"
                      value={activeCustomText.fontSize || 16}
                      onChange={(e) => {
                        const fontSize = Number(e.target.value);
                        setCanvasData(prev => ({
                          ...prev,
                          customTexts: prev.customTexts.map((ct, i) =>
                            i === selectedElement.customTextIndex ? { ...ct, fontSize } : ct
                          )
                        }));
                      }}
                      style={{ height: 32, padding: "0 8px", border: "1px solid var(--border-strong)" }}
                    />
                  </label>

                  <label className="col gap-1">
                    <span className="tiny muted">{lang === "id" ? "Warna Huruf" : "Font Color"}</span>
                    <div className="row gap-2">
                      <input
                        type="color"
                        value={activeCustomText.color || "#000000"}
                        onChange={(e) => {
                          const color = e.target.value;
                          setCanvasData(prev => ({
                            ...prev,
                            customTexts: prev.customTexts.map((ct, i) =>
                              i === selectedElement.customTextIndex ? { ...ct, color } : ct
                            )
                          }));
                        }}
                        style={{ width: 32, height: 32, padding: 0, border: "1px solid var(--border-strong)", cursor: "pointer" }}
                      />
                      <input
                        type="text"
                        value={activeCustomText.color || "#000000"}
                        onChange={(e) => {
                          const color = e.target.value;
                          setCanvasData(prev => ({
                            ...prev,
                            customTexts: prev.customTexts.map((ct, i) =>
                              i === selectedElement.customTextIndex ? { ...ct, color } : ct
                            )
                          }));
                        }}
                        style={{ flex: 1, height: 32, padding: "0 8px", border: "1px solid var(--border-strong)", fontFamily: "var(--font-mono)", fontSize: 12 }}
                      />
                    </div>
                  </label>

                  <div className="row between mt-1">
                    <span className="tiny muted">{lang === "id" ? "Gaya Huruf" : "Font Styles"}</span>
                    <div className="row gap-2">
                      <button
                        className={"btn sm" + (activeCustomText.fontWeight === 'bold' ? ' primary' : ' secondary')}
                        style={{ width: 28, height: 28, padding: 0, fontSize: 11 }}
                        onClick={() => setCanvasData(prev => ({ ...prev, customTexts: prev.customTexts.map((ct, i) => i === selectedElement.customTextIndex ? { ...ct, fontWeight: ct.fontWeight === 'bold' ? 'normal' : 'bold' } : ct) }))}
                      >B</button>
                      <button
                        className={"btn sm" + (activeCustomText.fontStyle === 'italic' ? ' primary' : ' secondary')}
                        style={{ width: 28, height: 28, padding: 0, fontSize: 11 }}
                        onClick={() => setCanvasData(prev => ({ ...prev, customTexts: prev.customTexts.map((ct, i) => i === selectedElement.customTextIndex ? { ...ct, fontStyle: ct.fontStyle === 'italic' ? 'normal' : 'italic' } : ct) }))}
                      >I</button>
                    </div>
                  </div>

                  <div className="row between mt-1">
                    <span className="tiny muted">{lang === "id" ? "Rataan" : "Alignment"}</span>
                    <div className="row gap-1">
                      {['left', 'center', 'right'].map(align => (
                        <button
                          key={align}
                          className={"btn sm" + (activeCustomText.align === align ? ' primary' : ' secondary')}
                          style={{ fontSize: "10px", textTransform: "capitalize", padding: "4px 8px", height: "auto" }}
                          onClick={() => setCanvasData(prev => ({ ...prev, customTexts: prev.customTexts.map((ct, i) => i === selectedElement.customTextIndex ? { ...ct, align } : ct) }))}
                        >{align}</button>
                      ))}
                    </div>
                  </div>

                  <button
                    className="btn sm outline red mt-2"
                    style={{ width: "100%" }}
                    onClick={() => handleDeleteCustomText(selectedElement.customTextIndex)}
                  >
                    <Icon name="trash" size={12} /> {lang === "id" ? "Hapus Elemen Ini" : "Delete This Element"}
                  </button>
                </>
              )}

              {!selectedElement.isSignature && selectedElement.key !== 'qrCode' && activeField && (
                <>
                  <label className="col gap-1">
                    <span className="tiny muted">{lang === "id" ? "Ukuran Huruf (px)" : "Font Size (px)"}</span>
                    <input
                      type="number"
                      value={activeField.fontSize || 14}
                      onChange={(e) => {
                        const fontSize = Number(e.target.value);
                        setCanvasData(prev => ({
                          ...prev,
                          fields: { ...prev.fields, [selectedElement.key]: { ...prev.fields[selectedElement.key], fontSize } },
                        }));
                      }}
                      style={{ height: 32, padding: "0 8px", border: "1px solid var(--border-strong)" }}
                    />
                  </label>

                  <label className="col gap-1">
                    <span className="tiny muted">{lang === "id" ? "Warna Huruf" : "Font Color"}</span>
                    <div className="row gap-2">
                      <input
                        type="color"
                        value={activeField.color || "#000000"}
                        onChange={(e) => {
                          const color = e.target.value;
                          setCanvasData(prev => ({
                            ...prev,
                            fields: { ...prev.fields, [selectedElement.key]: { ...prev.fields[selectedElement.key], color } },
                          }));
                        }}
                        style={{ width: 32, height: 32, padding: 0, border: "1px solid var(--border-strong)", cursor: "pointer" }}
                      />
                      <input
                        type="text"
                        value={activeField.color || "#000000"}
                        onChange={(e) => {
                          const color = e.target.value;
                          setCanvasData(prev => ({
                            ...prev,
                            fields: { ...prev.fields, [selectedElement.key]: { ...prev.fields[selectedElement.key], color } },
                          }));
                        }}
                        style={{ flex: 1, height: 32, padding: "0 8px", border: "1px solid var(--border-strong)", fontFamily: "var(--font-mono)", fontSize: 12 }}
                      />
                    </div>
                  </label>

                  <div className="row between mt-1">
                    <span className="tiny muted">{lang === "id" ? "Gaya Huruf" : "Font Styles"}</span>
                    <div className="row gap-2">
                      <button
                        className={"btn sm" + (activeField.fontWeight === 'bold' ? ' primary' : ' secondary')}
                        style={{ width: 28, height: 28, padding: 0, fontSize: 11 }}
                        onClick={() => {
                          const fontWeight = activeField.fontWeight === 'bold' ? 'normal' : 'bold';
                          setCanvasData(prev => ({
                            ...prev,
                            fields: { ...prev.fields, [selectedElement.key]: { ...prev.fields[selectedElement.key], fontWeight } },
                          }));
                        }}
                      >
                        B
                      </button>
                      <button
                        className={"btn sm" + (activeField.fontStyle === 'italic' ? ' primary' : ' secondary')}
                        style={{ width: 28, height: 28, padding: 0, fontSize: 11 }}
                        onClick={() => {
                          const fontStyle = activeField.fontStyle === 'italic' ? 'normal' : 'italic';
                          setCanvasData(prev => ({
                            ...prev,
                            fields: { ...prev.fields, [selectedElement.key]: { ...prev.fields[selectedElement.key], fontStyle } },
                          }));
                        }}
                      >
                        I
                      </button>
                    </div>
                  </div>

                  <div className="row between mt-1">
                    <span className="tiny muted">{lang === "id" ? "Rataan" : "Alignment"}</span>
                    <div className="row gap-1">
                      {['left', 'center', 'right'].map(align => (
                        <button
                          key={align}
                          className={"btn sm" + (activeField.align === align ? ' primary' : ' secondary')}
                          style={{ fontSize: "10px", textTransform: "capitalize", padding: "4px 8px", height: "auto" }}
                          onClick={() => {
                            setCanvasData(prev => ({
                              ...prev,
                              fields: { ...prev.fields, [selectedElement.key]: { ...prev.fields[selectedElement.key], align } },
                            }));
                          }}
                        >
                          {align}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {selectedElement.key === 'qrCode' && (
                <label className="col gap-1">
                  <span className="tiny muted">{lang === "id" ? "Ukuran QR (px)" : "QR Size (px)"}</span>
                  <input
                    type="number"
                    value={activeField?.size || 60}
                    onChange={(e) => {
                      const size = Number(e.target.value);
                      setCanvasData(prev => ({
                        ...prev,
                        fields: { ...prev.fields, qrCode: { ...prev.fields.qrCode, size } },
                      }));
                    }}
                    style={{ height: 32, padding: "0 8px", border: "1px solid var(--border-strong)" }}
                  />
                </label>
              )}

              {selectedElement.isSignature && activeSig && (
                <>
                  <button
                    className="btn sm outline red"
                    style={{ width: "100%", marginBottom: 4 }}
                    onClick={() => handleDeleteSignature(selectedElement.sigIndex)}
                  >
                    <Icon name="trash" size={12} /> {lang === "id" ? "Hapus Tanda Tangan Ini" : "Delete This Signature"}
                  </button>

                  <label className="col gap-1">
                    <span className="tiny muted">{lang === "id" ? "Nama Penandatangan" : "Signee Name"}</span>
                    <input
                      type="text"
                      value={activeSig.name || ""}
                      onChange={(e) => {
                        const name = e.target.value;
                        setCanvasData(prev => {
                          const next = { ...prev };
                          next.signatures = prev.signatures.map((sig, i) => 
                            i === selectedElement.sigIndex ? { ...sig, name } : sig
                          );
                          return next;
                        });
                      }}
                      style={{ height: 32, padding: "0 8px", border: "1px solid var(--border-strong)", fontSize: 13 }}
                    />
                  </label>

                  <label className="col gap-1">
                    <span className="tiny muted">{lang === "id" ? "Jabatan / Gelar" : "Title / Designation"}</span>
                    <input
                      type="text"
                      value={activeSig.title || ""}
                      onChange={(e) => {
                        const title = e.target.value;
                        setCanvasData(prev => {
                          const next = { ...prev };
                          next.signatures = prev.signatures.map((sig, i) => 
                            i === selectedElement.sigIndex ? { ...sig, title } : sig
                          );
                          return next;
                        });
                      }}
                      style={{ height: 32, padding: "0 8px", border: "1px solid var(--border-strong)", fontSize: 13 }}
                    />
                  </label>

                  <div>
                    <span className="tiny muted mb-1 block" style={{ display: "block" }}>{lang === "id" ? "Gambar Tanda Tangan" : "Signature Image"}</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleSigUpload(selectedElement.sigIndex, e)}
                      id={`sig-file-input-${selectedElement.sigIndex}`}
                      style={{ display: "none" }}
                    />
                    <div className="row gap-2">
                      <button
                        className="btn secondary sm"
                        style={{ flex: 1, height: 32 }}
                        onClick={() => document.getElementById(`sig-file-input-${selectedElement.sigIndex}`).click()}
                        disabled={uploadingSig}
                      >
                        {uploadingSig ? "..." : (lang === "id" ? "Unggah Gambar" : "Upload Image")}
                      </button>
                      {activeSig.imageUrl && (
                        <button
                          className="btn sm outline red"
                          style={{ height: 32 }}
                          onClick={() => {
                            setCanvasData(prev => {
                              const next = { ...prev };
                              next.signatures = prev.signatures.map((sig, i) => 
                                i === selectedElement.sigIndex ? { ...sig, imageUrl: "" } : sig
                              );
                              return next;
                            });
                          }}
                        >
                          <Icon name="trash" size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Toggle Fields Visibility list */}
        <div className="tile tile-pad" style={{ padding: 18 }}>
          <h4 className="h4 mb-2" style={{ fontSize: 14 }}>{lang === "id" ? "Daftar Elemen" : "Element List"}</h4>
          <div className="col gap-1" style={{ maxHeight: "320px", overflowY: "auto" }}>

            {/* System fields — show/hide only */}
            <div className="tiny muted mb-1" style={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>
              {lang === "id" ? "Data Sertifikat" : "Certificate Data"}
            </div>
            {Object.keys(canvasData.fields).map(key => {
              const field = canvasData.fields[key];
              const visible = field?.visible !== false;
              const isSelected = selectedElement?.key === key && !selectedElement?.isCustomText;
              const labelMap = {
                participantName: lang === "id" ? "Nama Peserta" : "Participant Name",
                certNo: lang === "id" ? "Nomor Sertifikat" : "Certificate Number",
                validUntil: lang === "id" ? "Berlaku Sampai" : "Valid Until",
                programName: lang === "id" ? "Nama Program" : "Program Name",
                oprcLevel: lang === "id" ? "Level Program" : "Program Level",
                issueDate: lang === "id" ? "Tanggal Terbit" : "Issue Date",
                company: lang === "id" ? "Nama Perusahaan" : "Company Name",
                nauticalCertNo: lang === "id" ? "No Sertifikat Nautical" : "Nautical Cert No",
                placeDateOfBirth: lang === "id" ? "Tempat & Tgl Lahir" : "Place & DOB",
                trainingStartDate: lang === "id" ? "Tanggal Mulai Pelatihan" : "Training Start Date",
                trainingEndDate: lang === "id" ? "Tanggal Selesai Pelatihan" : "Training End Date",
                trainingLocation: lang === "id" ? "Lokasi Pelatihan" : "Training Location",
                qrCode: "QR Code"
              };
              return (
                <div
                  key={key}
                  className="row between"
                  style={{ padding: "4px 6px", background: isSelected ? "var(--g-10)" : "transparent", borderRadius: "var(--r-sm)", cursor: "pointer" }}
                  onClick={() => setSelectedElement({ key, isSignature: false, sigIndex: null, isCustomText: false, customTextIndex: null })}
                >
                  <span className="small" style={{ fontWeight: isSelected ? 600 : 400, fontSize: 12 }}>{labelMap[key] || key}</span>
                  <input
                    type="checkbox"
                    checked={visible}
                    onChange={(e) => {
                      const v = e.target.checked;
                      // `next.fields[key]` di sini HARUS objek baru, bukan mutasi di tempat.
                      // Program yang belum punya template tersimpan sama-sama memakai
                      // DEFAULT_CANVAS_DATA sebagai state awal (lihat useEffect di atas) —
                      // sebelumnya baris ini menulis langsung ke field milik konstanta itu,
                      // jadi mencentang satu kotak untuk satu program diam-diam ikut
                      // mengubah tampilan "default" program lain yang juga belum disimpan.
                      setCanvasData(prev => ({
                        ...prev,
                        fields: { ...prev.fields, [key]: { ...prev.fields[key], visible: v } },
                      }));
                    }}
                    onClick={e => e.stopPropagation()}
                  />
                </div>
              );
            })}

            <div className="divider" style={{ margin: "6px 0" }} />

            {/* Signatures */}
            <div className="row between mb-1">
              <span className="tiny muted" style={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>
                {lang === "id" ? "Tanda Tangan" : "Signatures"}
              </span>
              <button className="btn sm secondary" style={{ height: 22, padding: "0 8px", fontSize: 11 }} onClick={handleAddSignature}>
                + {lang === "id" ? "Tambah" : "Add"}
              </button>
            </div>
            {canvasData.signatures.map((sig, idx) => {
              const visible = sig?.visible !== false;
              const isSelected = selectedElement?.isSignature && selectedElement?.sigIndex === idx;
              return (
                <div
                  key={sig.id}
                  className="row between"
                  style={{ padding: "4px 6px", background: isSelected ? "var(--g-10)" : "transparent", borderRadius: "var(--r-sm)", cursor: "pointer" }}
                  onClick={() => setSelectedElement({ key: sig.id, isSignature: true, sigIndex: idx, isCustomText: false, customTextIndex: null })}
                >
                  <span className="small" style={{ fontWeight: isSelected ? 600 : 400, fontSize: 12 }}>
                    {lang === "id" ? `Ttd ${idx + 1}` : `Sig ${idx + 1}`}{sig.name ? ` — ${sig.name}` : ""}
                  </span>
                  <div className="row gap-1" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={visible}
                      onChange={(e) => {
                        const v = e.target.checked;
                        setCanvasData(prev => ({ ...prev, signatures: prev.signatures.map((s, i) => i === idx ? { ...s, visible: v } : s) }));
                      }}
                    />
                    <button
                      className="btn sm outline red"
                      style={{ height: 20, width: 20, padding: 0, fontSize: 10 }}
                      onClick={() => handleDeleteSignature(idx)}
                      title={lang === "id" ? "Hapus" : "Delete"}
                    >×</button>
                  </div>
                </div>
              );
            })}

            <div className="divider" style={{ margin: "6px 0" }} />

            {/* Custom Texts */}
            <div className="row between mb-1">
              <span className="tiny muted" style={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>
                {lang === "id" ? "Teks Kustom" : "Custom Texts"}
              </span>
              <button className="btn sm secondary" style={{ height: 22, padding: "0 8px", fontSize: 11 }} onClick={handleAddCustomText}>
                + {lang === "id" ? "Tambah" : "Add"}
              </button>
            </div>
            {(canvasData.customTexts || []).length === 0 && (
              <p className="tiny muted" style={{ margin: 0, padding: "2px 6px" }}>
                {lang === "id" ? "Belum ada teks kustom." : "No custom texts yet."}
              </p>
            )}
            {(canvasData.customTexts || []).map((ct, idx) => {
              const visible = ct?.visible !== false;
              const isSelected = selectedElement?.isCustomText && selectedElement?.customTextIndex === idx;
              return (
                <div
                  key={ct.id}
                  className="row between"
                  style={{ padding: "4px 6px", background: isSelected ? "var(--g-10)" : "transparent", borderRadius: "var(--r-sm)", cursor: "pointer" }}
                  onClick={() => setSelectedElement({ key: ct.id, isSignature: false, sigIndex: null, isCustomText: true, customTextIndex: idx })}
                >
                  <span className="small" style={{ fontWeight: isSelected ? 600 : 400, fontSize: 12, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {ct.text || (lang === "id" ? "[Teks Kustom]" : "[Custom Text]")}
                  </span>
                  <div className="row gap-1" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={visible}
                      onChange={(e) => {
                        const v = e.target.checked;
                        setCanvasData(prev => ({ ...prev, customTexts: prev.customTexts.map((c, i) => i === idx ? { ...c, visible: v } : c) }));
                      }}
                    />
                    <button
                      className="btn sm outline red"
                      style={{ height: 20, width: 20, padding: 0, fontSize: 10 }}
                      onClick={() => handleDeleteCustomText(idx)}
                      title={lang === "id" ? "Hapus" : "Delete"}
                    >×</button>
                  </div>
                </div>
              );
            })}

          </div>
        </div>
      </div>
    </div>
  );
}

function QRCodeBlock({ token }) {
  const url = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(token)}`;
  return <img src={url} alt="QR Code" style={{ width: "100%", height: "100%", display: "block" }} />;
}

export { CertificateScreen, ProgressAnalyticsScreen }
